import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Sparkles, Users, Euro, TrendingUp, Award, Upload, Video, CheckCircle2, Link2 } from "lucide-react";
import { toast } from "sonner";
import api, { auth } from "@/lib/api";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";

const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" }) : "—";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [students, setStudents] = useState([]);
  const [txs, setTxs] = useState([]);
  const [videos, setVideos] = useState([]);
  const [uploading, setUploading] = useState(null);
  const [uploadPct, setUploadPct] = useState(0);
  const [ytInputs, setYtInputs] = useState({});
  const fileInputs = useRef({});
  const nav = useNavigate();

  const load = async () => {
    try {
      const [s, st, t, v] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/students"),
        api.get("/admin/transactions"),
        api.get("/admin/videos"),
      ]);
      setStats(s.data); setStudents(st.data); setTxs(t.data);
      setVideos(v.data.lessons);
      setYtInputs(Object.fromEntries(v.data.lessons.map((l) => [l.id, l.youtube || ""])));
    } catch { toast.error("Error al cargar datos"); }
  };

  const refreshVideos = async () => {
    const { data } = await api.get("/admin/videos");
    setVideos(data.lessons);
    setYtInputs(Object.fromEntries(data.lessons.map((l) => [l.id, l.youtube || ""])));
  };

  const saveYoutube = async (lessonId) => {
    const url = (ytInputs[lessonId] || "").trim();
    if (!url) { toast.error("Pega un enlace de YouTube"); return; }
    try {
      await api.post(`/admin/lessons/${lessonId}/youtube`, { youtube_url: url });
      toast.success("Enlace de YouTube guardado");
      await refreshVideos();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar el enlace");
    }
  };

  const CHUNK_SIZE = 10 * 1024 * 1024;
  const uploadVideo = async (lessonId, file) => {
    if (!file) return;
    setUploading(lessonId);
    setUploadPct(0);
    const uploadId = crypto.randomUUID();
    const total = Math.ceil(file.size / CHUNK_SIZE);
    try {
      for (let i = 0; i < total; i++) {
        const fd = new FormData();
        fd.append("file", file.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE));
        await api.post(
          `/admin/lessons/${lessonId}/video/chunk?upload_id=${uploadId}&chunk_index=${i}&total_chunks=${total}&filename=${encodeURIComponent(file.name)}`,
          fd,
          { timeout: 120000 }
        );
        setUploadPct(Math.round(((i + 1) / total) * 100));
      }
      toast.success("Vídeo subido correctamente");
      await refreshVideos();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al subir el vídeo");
    }
    setUploading(null);
  };

  useEffect(() => { load(); }, []);
  const logout = () => { auth.logout(); nav("/"); };

  const statCards = stats ? [
    { icon: Users, label: "Alumnas totales", value: stats.total_students },
    { icon: Award, label: "Plan Premium", value: stats.premium_students },
    { icon: TrendingUp, label: "Plan Básico", value: stats.basic_students },
    { icon: Euro, label: "Ingresos totales", value: `${stats.revenue_eur.toFixed(2)} €` },
  ] : [];

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[var(--bg-main)]">
      <header className="bg-white border-b border-[var(--border-subtle)] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="text-[var(--brand-primary)]" size={22}/>
            <span className="font-heading text-xl">Panel de administración</span>
          </div>
          <button data-testid="admin-logout" onClick={logout} className="btn-outline-brand !py-2 !px-4 text-sm"><LogOut size={16}/> Salir</button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {statCards.map((c, i) => (
            <div key={i} data-testid={`stat-${i}`} className="card-luxe p-5">
              <c.icon className="text-[var(--brand-primary)] mb-2" size={22}/>
              <p className="text-xs uppercase tracking-widest text-[var(--text-muted)]">{c.label}</p>
              <p className="font-heading text-3xl mt-1">{c.value}</p>
            </div>
          ))}
        </div>

        <Tabs defaultValue="students">
          <TabsList data-testid="admin-tabs" className="mb-4">
            <TabsTrigger value="students" data-testid="tab-students">Alumnas</TabsTrigger>
            <TabsTrigger value="transactions" data-testid="tab-transactions">Transacciones</TabsTrigger>
            <TabsTrigger value="videos" data-testid="tab-videos">Vídeos del curso</TabsTrigger>
          </TabsList>

          <TabsContent value="students">
            <div className="card-luxe overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Plan</TableHead>
                    <TableHead>Registrada</TableHead>
                    <TableHead className="w-64">Progreso</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {students.length === 0 && (
                    <TableRow><TableCell colSpan={4} className="text-center py-8 text-[var(--text-muted)]">Aún no hay alumnas.</TableCell></TableRow>
                  )}
                  {students.map((s) => (
                    <TableRow key={s.user_id} data-testid={`student-row-${s.user_id}`}>
                      <TableCell className="font-medium">{s.email}</TableCell>
                      <TableCell>
                        <Badge variant={s.tier === "premium_course" ? "default" : "secondary"}>{s.tier_name}</Badge>
                      </TableCell>
                      <TableCell className="text-sm text-[var(--text-muted)]">{fmtDate(s.created_at)}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Progress value={s.progress_pct} className="h-2 flex-1"/>
                          <span className="text-xs text-[var(--text-muted)] w-28 text-right">{s.completed_lessons}/{s.total_lessons} ({s.progress_pct}%)</span>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="transactions">
            <div className="card-luxe overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Fecha</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Plan</TableHead>
                    <TableHead>Importe</TableHead>
                    <TableHead>Estado</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {txs.length === 0 && (
                    <TableRow><TableCell colSpan={5} className="text-center py-8 text-[var(--text-muted)]">Sin transacciones.</TableCell></TableRow>
                  )}
                  {txs.map((t) => (
                    <TableRow key={t.session_id} data-testid={`tx-row-${t.session_id}`}>
                      <TableCell className="text-sm">{fmtDate(t.created_at)}</TableCell>
                      <TableCell>{t.email}</TableCell>
                      <TableCell>{t.tier_name}</TableCell>
                      <TableCell>{((t.amount || 0) / 100).toFixed(2)} {(t.currency || "EUR").toUpperCase()}</TableCell>
                      <TableCell>
                        <Badge variant={t.payment_status === "paid" ? "default" : t.payment_status === "pending" ? "secondary" : "destructive"}>
                          {t.payment_status}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
          <TabsContent value="videos">
            <div className="card-luxe p-6">
              <div className="flex items-center gap-2 mb-1">
                <Video className="text-[var(--brand-primary)]" size={20}/>
                <h3 className="font-heading text-2xl">Vídeos de las lecciones</h3>
              </div>
              <p className="text-sm text-[var(--text-muted)] mb-6">Pega un enlace de YouTube (opción más rápida, recomendada) o sube un MP4. Las alumnas verán el vídeo en su aula al instante.</p>
              <ul className="divide-y divide-[var(--border-subtle)]">
                {videos.map((v) => (
                  <li key={v.id} className="py-3">
                    <div className="flex items-center gap-3 flex-wrap">
                      {v.youtube
                        ? <Link2 size={18} className="text-[var(--brand-primary)] flex-shrink-0"/>
                        : v.has_video
                          ? <CheckCircle2 size={18} className="text-green-600 flex-shrink-0"/>
                          : <Video size={18} className="text-[var(--text-muted)] flex-shrink-0"/>}
                      <div className="flex-1 min-w-[200px]">
                        <p className="text-sm font-medium">{v.title}</p>
                        <p className="text-xs text-[var(--text-muted)]">{v.module}</p>
                      </div>
                      <span data-testid={`video-status-${v.id}`} className="text-xs text-[var(--text-muted)]">
                        {v.youtube ? "YouTube" : v.has_video ? `${v.filename} · ${(v.size / 1024 / 1024).toFixed(1)} MB` : "Vídeo demo"}
                      </span>
                      <input
                        type="file"
                        accept="video/mp4"
                        className="hidden"
                        ref={(el) => (fileInputs.current[v.id] = el)}
                        onChange={(e) => { uploadVideo(v.id, e.target.files[0]); e.target.value = ""; }}
                      />
                      <button
                        data-testid={`upload-video-${v.id}`}
                        onClick={() => fileInputs.current[v.id]?.click()}
                        disabled={uploading === v.id}
                        className="btn-outline-brand !py-1.5 !px-4 text-xs"
                      >
                        <Upload size={14}/> {uploading === v.id ? `Subiendo… ${uploadPct}%` : v.has_video ? "Reemplazar" : "Subir MP4"}
                      </button>
                    </div>
                    <div className="flex items-center gap-2 mt-2 pl-8">
                      <Input
                        data-testid={`youtube-input-${v.id}`}
                        placeholder="https://youtu.be/…"
                        value={ytInputs[v.id] || ""}
                        onChange={(e) => setYtInputs({ ...ytInputs, [v.id]: e.target.value })}
                        className="h-8 text-xs flex-1"
                      />
                      <button
                        data-testid={`save-youtube-${v.id}`}
                        onClick={() => saveYoutube(v.id)}
                        className="btn-outline-brand !py-1.5 !px-3 text-xs whitespace-nowrap"
                      >
                        <Link2 size={14}/> Guardar enlace
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
